<script setup lang="ts">
    import { OrthographicCamera } from 'three';
    import { stopAll } from '@/util/stopHandle';
    import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
    import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
    import { updatePlanner } from '@/views/3d-planner/updatePlanner';
    import { updateCup } from '@/views/3d-planner/fitting/updateCup';
    import { updateStem } from '@/views/3d-planner/fitting/updateStem';
    import { plannerSceneContext } from '@/views/3d-planner/scene/plannerSceneContext';
    import { updatePlannerScene } from '@/views/3d-planner/scene/updatePlannerScene';
    import { updateLtResectionVisualisation } from '@/views/3d-planner/measurements/updateLtResectionVisualisation';
    import { useResectionHeightViewModel } from '@/views/3d-planner/measurements/useResectionHeightViewModel';
    import { useScene } from '@/lib/threeScene/scene/useScene';
    import { attachRenderingState } from '@/lib/threeScene/scene/attachRenderingState';
    import { useCameraKeyboardShortcuts } from '@/views/3d-planner/composables/useCameraKeyboardShortcuts';

    const model = usePlannerStore();
    const scene = usePlannerSceneViewModel();
    // The LT-resection feature's 3D decoration is wired here (the View owns feature-VM → scene-module
    // wiring) so the scene module stays store-free — see docs/threejs-practices.md.
    const resection = useResectionHeightViewModel();

    const { containerRef, canvasRef } = useScene({
        camera: () => new OrthographicCamera(),
        sceneContext: () => plannerSceneContext(scene.sceneState),
        setup: ({ canvas, container, sceneContext, camera, runtime }) => stopAll(
            updateCup(model, scene),
            updateStem(model, scene),
            updatePlanner(model, scene),
            updatePlannerScene(model, scene, sceneContext),
            updateLtResectionVisualisation(sceneContext, scene, () => resection.result),
            attachRenderingState({
                runtime,
                state: scene.rendering,
                scene: sceneContext.scene,
                camera,
                container,
                canvas,
            }),
            useCameraKeyboardShortcuts(scene.rendering.camera).stop,
        ),
    });
</script>

<template>
    <div ref="containerRef" class="viewport-container">
        <canvas ref="canvasRef" class="viewport-canvas" />
    </div>
</template>

<style scoped>
    .viewport-container {
        position: absolute;
        width: 100%;
        height: 100%;
    }

    .viewport-canvas {
        position: absolute;
    }
</style>
